"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DiscoverySession = void 0;
const utils_1 = require("@streamr/utils");
const uuid_1 = require("uuid");
const identifiers_1 = require("../../identifiers");
const PeerManager_1 = require("../PeerManager");
const getClosestNodes_1 = require("../contact/getClosestNodes");
const logger = new utils_1.Logger(module);
class DiscoverySession {
    id = (0, uuid_1.v4)();
    noProgressCounter = 0;
    ongoingRequests = new Set();
    doneGate = new utils_1.Gate(false);
    options;
    constructor(options) {
        this.options = options;
    }
    addContacts(contacts) {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return;
        }
        for (const contact of contacts) {
            this.options.peerManager.addContact(contact);
        }
    }
    async fetchClosestNeighborsFromRemote(peerDescriptor) {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return [];
        }
        const nodeId = (0, identifiers_1.toNodeId)(peerDescriptor);
        logger.trace(`Getting closest neighbors from remote: ${nodeId}`);
        this.options.contactedPeers.add(nodeId);
        const remote = this.options.createDhtNodeRpcRemote(peerDescriptor);
        const returnedContacts = await remote.getClosestPeers(this.options.targetId);
        this.options.peerManager.setContactActive(nodeId);
        return returnedContacts;
    }
    onRequestSucceeded(nodeId, contacts) {
        if (!this.ongoingRequests.has(nodeId)) {
            return;
        }
        this.ongoingRequests.delete(nodeId);
        const targetId = (0, identifiers_1.toDhtAddressRaw)(this.options.targetId);
        const oldClosestNeighbor = this.getClosestNeighbor();
        const oldClosestDistance = (0, PeerManager_1.getDistance)(targetId, oldClosestNeighbor.nodeId);
        this.addContacts(contacts);
        const newClosestNeighbor = this.getClosestNeighbor();
        const newClosestDistance = (0, PeerManager_1.getDistance)(targetId, newClosestNeighbor.nodeId);
        if (newClosestDistance >= oldClosestDistance) {
            this.noProgressCounter++;
        }
    }
    getClosestNeighbor() {
        return (0, getClosestNodes_1.getClosestNodes)(this.options.targetId, this.options.peerManager.getNeighbors().map((n) => n.getPeerDescriptor()), { maxCount: 1 })[0];
    }
    onRequestFailed(nodeId) {
        if (!this.ongoingRequests.has(nodeId)) {
            return;
        }
        this.ongoingRequests.delete(nodeId);
        this.options.peerManager.removeContact(nodeId);
    }
    findMoreContacts() {
        if (this.options.abortSignal.aborted || this.doneGate.isOpen()) {
            return;
        }
        const uncontacted = (0, getClosestNodes_1.getClosestNodes)(this.options.targetId, Array.from(this.options.peerManager.getNearbyContacts().getAllContactsInUndefinedOrder(), (c) => c.getPeerDescriptor()), {
            maxCount: this.options.parallelism,
            excludedNodeIds: this.options.contactedPeers
        });
        if ((uncontacted.length === 0 && this.ongoingRequests.size === 0) || (this.noProgressCounter >= this.options.noProgressLimit)) {
            this.doneGate.open();
            return;
        }
        for (const node of uncontacted) {
            if (this.ongoingRequests.size >= this.options.parallelism) {
                break;
            }
            const nodeId = (0, identifiers_1.toNodeId)(node);
            this.ongoingRequests.add(nodeId);
            // eslint-disable-next-line promise/catch-or-return
            this.fetchClosestNeighborsFromRemote(node)
                .then((contacts) => this.onRequestSucceeded(nodeId, contacts))
                .catch(() => this.onRequestFailed(nodeId))
                .finally(() => {
                this.findMoreContacts();
            });
        }
    }
    async findClosestNodes(timeout) {
        if (this.options.peerManager.getNearbyContactCount(this.options.contactedPeers) === 0) {
            return;
        }
        setImmediate(() => {
            this.findMoreContacts();
        });
        await (0, utils_1.withTimeout)(this.doneGate.waitUntilOpen(), timeout, 'discovery session timed out', this.options.abortSignal);
    }
}
exports.DiscoverySession = DiscoverySession;
//# sourceMappingURL=DiscoverySession.js.map